import { DEVICE_TYPES } from "../domain/models";
import { getAvailableUpdates } from "../constants/firmware";
import { DiagnosticsService } from "../services/diagnostics";

// availableUpdates: already-filtered firmware update entries (pass from call site)
// nodes: regular (non-gateway) nodes only
// devices: all devices
export function buildNotifications({ availableUpdates, nodes, devices }) {
  const items = [];

  // Firmware update notifications (one per node with a pending update)
  availableUpdates.forEach((entry) => {
    items.push({
      id: `fw-${entry.nodeId}`,
      severity: "info",
      icon: "system_update",
      title: "Firmware update available",
      body: `${entry.nodeName} — ${entry.currentVersion} → ${entry.availableVersion}`,
      actionLabel: "View updates",
      target: "firmware",
    });
  });

  // Node-offline notifications for nodes that are not responding
  nodes
    .filter((n) => n.status === "Error")
    .forEach((n) => {
      items.push({
        id: `node-err-${n.id}`,
        severity: "critical",
        icon: "cancel",
        title: "Node offline",
        body: `${n.name} is not responding on the CAN bus`,
        actionLabel: "View diagnostics",
        target: "diagnostics",
      });
    });

  // Low-battery notifications for BMS devices
  devices
    .filter((d) => d.type === DEVICE_TYPES.BMS && (d.metrics?.soc ?? 100) < 20)
    .forEach((d) => {
      items.push({
        id: `bms-soc-${d.id}`,
        severity: "warning",
        icon: "battery_alert",
        title: "Low battery charge",
        body: `${d.name} — ${d.metrics.soc}% remaining`,
      });
    });

  // DTC notifications — only for nodes that are present in the current network scan
  const nodeIds = new Set(nodes.map((n) => n.id));
  DiagnosticsService.getDtcs()
    .filter((d) => nodeIds.has(d.nodeId))
    .forEach((d) => {
      items.push({
        id: `dtc-${d.id}`,
        severity: d.severity,
        icon: d.severity === "critical" ? "error" : "warning",
        title: `DTC ${d.id}`,
        body: `${d.node} — ${d.description}`,
        actionLabel: "View diagnostics",
        target: "diagnostics",
      });
    });

  const order = { critical: 0, warning: 1, info: 2 };
  return items.sort((a, b) => order[a.severity] - order[b.severity]);
}

// Convenience wrapper — takes raw state, computes availableUpdates internally
export function buildNotificationsFromState({ updatedFirmware, nodes, devices }) {
  const regularNodes = nodes.filter(
    (n) => n.category !== "Gateway" && n.id !== "bt-bridge"
  );
  const availableUpdates = getAvailableUpdates(updatedFirmware).filter((entry) =>
    regularNodes.some((n) => n.id === entry.nodeId)
  );
  return { notifications: buildNotifications({ availableUpdates, nodes: regularNodes, devices }), regularNodes };
}
